import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, Share2, Image as ImageIcon, CloudOff, Loader2 } from "lucide-react";

export default function StoryWallMoment() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("story_wall_moment_title"));
  const [moment, setMoment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(false);
    try {
      const { data } = await api.get(`/public/story-wall/moment/${id}`);
      setMoment(data?.moment || null);
    } catch { setMoment(null); setError(true); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const shareUrl = `${API_BASE}/public/story-wall/moment/${id}/share?locale=${lang}`;
  const factCardUrl = `${API_BASE}/fact-cards/story-moment/${id}?locale=${lang}`;

  const doShare = async () => {
    if (!moment) return;
    const body = t("story_wall_share_body", { title: moment.title });
    try {
      if (navigator.share) await navigator.share({ title: moment.title, text: body, url: shareUrl });
      else { await navigator.clipboard.writeText(shareUrl); toast.success(t("story_wall_share_copied"), { description: t("story_wall_share_copied_body") }); }
    } catch { /* cancelled */ }
  };

  return (
    <div className="mx-auto max-w-xl space-y-4" data-testid="story-wall-moment">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/story-wall")} data-testid="swm-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="flex-1 font-display text-2xl text-foreground">{t("story_wall_moment_title")}</h1>
        {moment ? (
          <button onClick={doShare} data-testid="swm-share" className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary"><Share2 className="h-5 w-5" /></button>
        ) : null}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : !moment || error ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <CloudOff className="h-10 w-10 text-muted-foreground/50" />
          <p className="font-display text-lg text-foreground">{t("story_wall_moment_missing_title")}</p>
          <p className="max-w-sm text-sm text-muted-foreground">{t("story_wall_moment_missing_body")}</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border bg-card p-6" style={{ borderTopWidth: 6, borderTopColor: moment.color_accent || "hsl(var(--primary))" }}>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{t(`story_wall_kind_${moment.kind}`)}</p>
            {moment.metric_label ? <p className="mt-2 font-display text-5xl text-foreground">{moment.metric_label}</p> : null}
            <p className="mt-2 font-display text-2xl leading-tight text-foreground">{moment.title}</p>
            <div className="mt-3 space-y-2.5">
              {(moment.body || "").split("\n\n").filter((p) => p.trim()).map((p, i) => (
                <p key={i} className="text-[15px] italic leading-relaxed text-foreground">{p.trim()}</p>
              ))}
            </div>
            <p className="mt-4 border-t border-border pt-3 text-sm text-muted-foreground">
              {moment.coach_display ? t("story_wall_by_coach", { name: moment.coach_display }) : t("story_wall_by_coach_anonymous")}
              {moment.client_display ? ` · ${moment.client_display}` : ""}
            </p>
          </div>

          <div className="space-y-2">
            <button onClick={doShare} data-testid="swm-share-cta" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground"><Share2 className="h-4 w-4" /> {t("story_wall_share_cta")}</button>
            <a href={factCardUrl} target="_blank" rel="noopener noreferrer" data-testid="swm-open-card" className="flex w-full items-center justify-center gap-2 rounded-full border border-primary bg-primary/10 py-3 font-semibold text-primary"><ImageIcon className="h-4 w-4" /> {t("story_wall_share_open_card")}</a>
          </div>
          <p className="text-center text-[11px] italic leading-relaxed text-muted-foreground">{t("story_wall_privacy_note")}</p>
        </>
      )}
    </div>
  );
}
